
import { useState } from "react";
import { useAuth } from "@/contexts/auth";
import { useToast } from "./use-toast";
import { RefundFormValues } from "./use-refund-form";
import { useRequestLimitCheck } from "./useRequestLimitCheck";
import { saveRequestToDatabase, createDemoRequest } from "@/utils/refund-request/database-operations";
import { generateResponse } from "@/utils/refund-request/response-generation";
import { useNavigate } from "react-router-dom";

export const useRefundSubmission = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isLimitReached, checkRequestLimit, isProTier } = useRequestLimitCheck();

  const handleSubmit = async (
    values: RefundFormValues, 
    userRegion: string | null,
    messageFormat: "email" | "chat" = "email"
  ) => {
    setIsSubmitting(true);
    
    try {
      // For authenticated users
      if (user) {
        // Only check limits for free tier users
        if (!isProTier) {
          // Re-check limits to prevent bypassing the limit
          await checkRequestLimit();
          
          if (isLimitReached) {
            toast({
              title: "Monthly limit reached",
              description: "You've reached your monthly limit for free refund requests. Please upgrade to Pro for unlimited requests.",
              variant: "destructive",
            });
            setIsSubmitting(false);
            return null;
          }
        }
        
        // Save request to database for authenticated users
        const savedRequest = await saveRequestToDatabase(user.id, values, userRegion, messageFormat);
        
        // Generate AI response
        if (savedRequest) {
          await generateResponse(savedRequest, user);
          
          // Navigate to the conversation view
          navigate(`/conversation/${savedRequest.id}`);
          return savedRequest;
        }
      } 
      // For non-authenticated users (demo mode)
      else {
        // Create a demo request (not saved to database)
        const demoRequest = createDemoRequest(values, userRegion, messageFormat);
        
        // Generate AI response for demo
        await generateResponse(demoRequest, null);
        
        // Save demo request to session storage for later retrieval after signup
        sessionStorage.setItem('demoRefundRequest', JSON.stringify({
          ...demoRequest,
          formValues: values,
          userRegion,
          messageFormat // Ensure message format is preserved
        }));
        
        // Navigate to preview page
        navigate("/preview");
        return demoRequest;
      }
      
      return null;
    } catch (error) {
      console.error("Error submitting refund request:", error);
      toast({
        title: "Submission failed",
        description: "There was an error submitting your request. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleSubmit
  };
};
